import { lazy, Suspense, useEffect, useState } from 'react';
import { apiFetchBlob } from '../lib/api.js';
import { Button } from '../components/Button.js';
import { ErrorBoundary } from '../components/ErrorBoundary.js';
import { Icon } from '../components/Icon.js';
import { useDismiss } from '../util/useDismiss.js';
import styles from './ResumeDialog.module.css';

/**
 * Lazily, because pdf.js is far larger than the rest of this route put together
 * and most sessions never open a résumé. The import is what splits it into its
 * own chunk; a static one would put it in the initial chat bundle.
 */
const PdfViewer = lazy(() => import('./PdfViewer.js'));

/**
 * The candidate's résumé, in place.
 *
 * The file comes from our own BFF rather than from ajt2.5 directly, so it
 * arrives on this origin and can be rendered here instead of thrown into a tab.
 * That is also why the URL is not a prop: there isn't one to hold. The panel
 * knows only that a résumé exists; the bytes are fetched when the employer asks
 * for them, and released when they close.
 */
type State =
  | { phase: 'loading' }
  | ({ phase: 'ready'; fileName: string } & Ready)
  | { phase: 'error'; message: string; retryable: boolean };

/**
 * What a ready résumé carries, which is no longer one shape for all three: a
 * PDF is handed to pdf.js as bytes, an image still needs an object URL for
 * `<img src>`, and a type we decline to show needs neither.
 */
type Ready = { kind: 'pdf'; blob: Blob } | { kind: 'image'; url: string } | { kind: 'none' };

type Kind = Ready['kind'];

/**
 * How to render a blob of this type — an allowlist, not a fallthrough, and the
 * only place that decision is made.
 *
 * Neither branch can execute what it is given. A PDF is parsed by pdf.js and
 * painted onto a canvas — the bytes are never navigated to, so nothing in them
 * is ever a document. An `<img>` does not run script inside an SVG the way a
 * frame would. A file that could carry script is downloaded rather than shown.
 *
 * No résumé today can be one — every ajt2.5 upload validator is limited to
 * pdf/doc/docx/jpeg/png — which is exactly why this must not be relaxed to
 * `image/*` later on the grounds that nothing bad has happened.
 */
function kindOf(mime: string): Kind {
  const type = mime.split(';')[0]?.trim().toLowerCase() ?? '';
  if (type === 'application/pdf') return 'pdf';
  if (['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(type)) return 'image';
  return 'none';
}

export function ResumeDialog({
  channelId,
  candidateName,
  onClose,
}: {
  channelId: string;
  candidateName: string;
  onClose: () => void;
}) {
  useDismiss(true, onClose);
  const [state, setState] = useState<State>({ phase: 'loading' });
  /**
   * Bumped by the retry, and in the fetch effect's deps so that it re-runs.
   *
   * The retry used to set `phase: 'loading'` and nothing else. The effect was
   * keyed on `[channelId]` alone, which had not changed, so no fetch ever
   * followed and the dialog spun until it was closed.
   */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;

    // Set here rather than by the retry, so one place owns the transition into
    // a fetch — including the one caused by arriving on a different channel.
    setState({ phase: 'loading' });

    apiFetchBlob(`/api/conversations/${encodeURIComponent(channelId)}/resume`)
      .then(({ blob, fileName }) => {
        if (!alive) return;
        const name = fileName ?? 'Resume';
        const kind = kindOf(blob.type);
        // Only the image branch still needs a URL. A PDF is read from its bytes
        // and a declined type is never loaded at all, so neither creates one —
        // which is the same reason `frame-src blob:` came out of the CSP.
        if (kind === 'image') {
          objectUrl = URL.createObjectURL(blob);
          setState({ phase: 'ready', kind, url: objectUrl, fileName: name });
        } else if (kind === 'pdf') {
          setState({ phase: 'ready', kind, blob, fileName: name });
        } else {
          setState({ phase: 'ready', kind, fileName: name });
        }
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const e = err as { message?: string; retryable?: boolean };
        setState({
          phase: 'error',
          message: e.message ?? 'Could not load the résumé.',
          retryable: e.retryable === true,
        });
      });

    return () => {
      alive = false;
      // The blob is held by this tab until it is revoked, so this runs on close
      // and on unmount, not on one of them.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [channelId, attempt]);

  const [saving, setSaving] = useState(false);

  /**
   * The file the candidate actually submitted, not our render of it.
   *
   * A second request rather than the previewed blob, because a Word résumé is
   * converted on the way here and the conversion is lossy — saving that render
   * would leave the employer holding the only copy, mangled. `?original=1`
   * turns the conversion off. A PDF résumé is never converted, so this returns
   * the same bytes either way.
   *
   * Fetched rather than linked: the session token rides in a header, so a bare
   * <a href> to the API would 401.
   */
  async function saveOriginal() {
    setSaving(true);
    try {
      const { blob, fileName } = await apiFetchBlob(
        `/api/conversations/${encodeURIComponent(channelId)}/resume?original=1`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'Resume';
      a.click();
      // Not revoked synchronously: Chrome cancels a download whose blob is
      // released in the same tick as the click.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      // The preview is still on screen and the panel already reports upstream
      // failures; a failed save does not warrant replacing it with an error.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={`Résumé — ${candidateName}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>{state.phase === 'ready' ? state.fileName : 'Resume'}</h2>
          <div className={styles.actions}>
            {state.phase === 'ready' && (
              // A button, not a link to `state.url`: that blob may be our PDF
              // render of a Word file, and a render is for reading, not keeping.
              // Downloading re-requests the résumé untouched.
              <button
                type="button"
                className={styles.download}
                onClick={() => void saveOriginal()}
                disabled={saving}
              >
                <Icon name="file" size={15} />
                {saving ? 'Preparing…' : 'Download'}
              </button>
            )}
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label="Close résumé"
            >
              <Icon name="close" size={20} />
            </button>
          </div>
        </div>

        <div className={styles.body}>
          {state.phase === 'loading' && <p className={styles.note}>Loading résumé…</p>}

          {state.phase === 'error' && (
            <div className={styles.note}>
              <p>{state.message}</p>
              {state.retryable && (
                <Button variant="ghost" onClick={() => setAttempt((n) => n + 1)}>
                  Try again
                </Button>
              )}
            </div>
          )}

          {state.phase === 'ready' && state.kind === 'pdf' && (
            // Painted onto canvases rather than framed. This was an <iframe>
            // pointed at a blob: URL, which Safari renders as an empty box —
            // WebKit's PDF viewer wants a real navigable resource — and which
            // iOS renders as at most a first page however the file is served.
            // Serving it differently could not fix that, so the element is gone
            // rather than repointed.
            // Its own boundary: a viewer that fails to load or run (e.g. a browser
            // pdf.js cannot support) must cost the preview, not the whole panel.
            <ErrorBoundary
              where="resume-viewer"
              fallback={
                <p className={styles.note}>
                  This résumé can&rsquo;t be previewed. Download it to open it.
                </p>
              }
            >
              <Suspense fallback={<p className={styles.note}>Loading viewer…</p>}>
                <PdfViewer blob={state.blob} label={state.fileName} />
              </Suspense>
            </ErrorBoundary>
          )}

          {state.phase === 'ready' && state.kind === 'image' && (
            <img className={styles.image} src={state.url} alt={state.fileName} />
          )}

          {state.phase === 'ready' && state.kind === 'none' && (
            <p className={styles.note}>
              This file type can&rsquo;t be previewed in the browser. Download it to open it.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
